import React, { useMemo, useState } from 'react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { BellIcon, CalendarIcon, MessageCircleIcon, UsersIcon, ClockIcon, CheckIcon, XIcon, MapPinIcon, SparklesIcon, TrendingUpIcon } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import { useAlerts } from '../contexts/AlertsContext';
interface AlertsProps {
  onNavigateToNetworking?: () => void;
  onNavigateToAgenda?: () => void;
  onNavigateToSchedule?: () => void;
}
export function Alerts({
  onNavigateToNetworking,
  onNavigateToAgenda,
  onNavigateToSchedule
}: AlertsProps) {
  const {
    currentEvent
  } = useEvent();
  const {
    alerts,
    unreadCount,
    actionableCount,
    markAsRead,
    markAllAsRead
  } = useAlerts();
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'unread' | 'actionable'>('all');
  const filteredAlerts = useMemo(() => {
    let filtered = alerts;
    if (selectedFilter === 'unread') {
      filtered = filtered.filter(a => !a.read);
    } else if (selectedFilter === 'actionable') {
      filtered = filtered.filter(a => a.actionable);
    }
    return filtered;
  }, [alerts, selectedFilter]);
  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'session-reminder':
      case 'session-starting':
        return CalendarIcon;
      case 'meeting-invite':
      case 'meeting-accepted':
      case 'meeting-declined':
        return UsersIcon;
      case 'chat-message':
        return MessageCircleIcon;
      case 'event-update':
        return SparklesIcon;
      default:
        return BellIcon;
    }
  };
  const getAlertColor = (type: string) => {
    switch (type) {
      case 'session-reminder':
      case 'session-starting':
        return currentEvent.colors.primary;
      case 'meeting-invite':
        return '#f59e0b';
      case 'meeting-accepted':
        return '#10b981';
      case 'meeting-declined':
        return '#ef4444';
      case 'chat-message':
        return '#3b82f6';
      case 'event-update':
        return '#8b5cf6';
      default:
        return '#6b7280';
    }
  };
  const handleAlertAction = (alert: any) => {
    markAsRead(alert.id);
    switch (alert.type) {
      case 'session-reminder':
      case 'session-starting':
        if (onNavigateToSchedule) onNavigateToSchedule();
        break;
      case 'meeting-invite':
      case 'meeting-accepted':
        if (onNavigateToNetworking) onNavigateToNetworking();
        break;
      case 'chat-message':
        if (onNavigateToNetworking) onNavigateToNetworking();
        break;
      case 'event-update':
        if (onNavigateToAgenda) onNavigateToAgenda();
        break;
    }
  };
  return <div className="pb-24" data-id="element-3195">
      {/* Header Stats */}
      <div className="sticky top-[88px] z-30 bg-white border-b border-gray-200 px-4 py-4" data-id="element-3196">
        <div className="flex items-center justify-between mb-4" data-id="element-3197">
          <div data-id="element-3198">
            <h2 className="text-lg font-bold font-gotham text-gray-900" data-id="element-3199">
              Notifications
            </h2>
            <p className="text-sm text-gray-600 font-gotham" data-id="element-3200">
              {unreadCount} unread, {actionableCount} need action
            </p>
          </div>
          {unreadCount > 0 && <Button variant="outline" size="sm" onClick={markAllAsRead} data-id="element-3201">
              <CheckIcon className="w-4 h-4 mr-1 inline" data-id="element-3202" />
              Mark all read
            </Button>}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2" data-id="element-3203">
          {[{
          id: 'all',
          label: 'All',
          count: alerts.length
        }, {
          id: 'unread',
          label: 'Unread',
          count: unreadCount
        }, {
          id: 'actionable',
          label: 'Action Needed',
          count: actionableCount
        }].map(filter => <button key={filter.id} onClick={() => setSelectedFilter(filter.id as any)} className={`flex-1 px-4 py-2 rounded-lg font-medium font-gotham transition-all text-sm ${selectedFilter === filter.id ? 'text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} style={selectedFilter === filter.id ? {
          backgroundColor: currentEvent.colors.primary,
          color: currentEvent.id === 'active' ? '#17313a' : 'white'
        } : {}} data-id="element-3204">
              {filter.label}
              {filter.count > 0 && <span className="ml-1 opacity-75" data-id="element-3205">({filter.count})</span>}
            </button>)}
        </div>
      </div>

      {/* Alerts List */}
      <div className="px-4 py-4 space-y-3" data-id="element-3206">
        {filteredAlerts.length === 0 ? <div className="text-center py-12" data-id="element-3207">
            <BellIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" data-id="element-3208" />
            <h3 className="text-lg font-bold text-gray-900 font-gotham mb-2" data-id="element-3209">
              {selectedFilter === 'unread' ? 'All caught up!' : 'No notifications'}
            </h3>
            <p className="text-gray-600 font-gotham" data-id="element-3210">
              {selectedFilter === 'unread' ? "You've read all your notifications" : 'Check back later for updates'}
            </p>
          </div> : filteredAlerts.map(alert => {
        const Icon = getAlertIcon(alert.type);
        const color = getAlertColor(alert.type);
        return <Card key={alert.id} padding="md" className={`transition-all ${!alert.read ? 'bg-blue-50 border-l-4' : ''}`} style={!alert.read ? {
          borderLeftColor: color
        } : {}} data-id="element-3211">
                <div className="flex gap-3" data-id="element-3212">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{
              backgroundColor: `${color}20`,
              color: color
            }} data-id="element-3213">
                    <Icon className="w-5 h-5" data-id="element-3214" />
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-3215">
                    <div className="flex items-start justify-between gap-2 mb-1" data-id="element-3216">
                      <h4 className="font-bold text-gray-900 font-gotham text-sm" data-id="element-3217">
                        {alert.title}
                      </h4>
                      <span className="text-xs text-gray-500 font-gotham flex-shrink-0" data-id="element-3218">
                        {alert.timestamp}
                      </span>
                    </div>

                    <p className="text-sm text-gray-700 font-gotham mb-3" data-id="element-3219">
                      {alert.message}
                    </p>

                    <div className="flex items-center gap-2" data-id="element-3220">
                      {alert.actionable && <Button variant="primary" size="sm" onClick={() => handleAlertAction(alert)} data-id="element-3221">
                          View
                        </Button>}
                      {!alert.read && <Button variant="outline" size="sm" onClick={() => markAsRead(alert.id)} data-id="element-3222">
                          <CheckIcon className="w-3 h-3 mr-1 inline" data-id="element-3223" />
                          Mark read
                        </Button>}
                    </div>
                  </div>
                </div>
              </Card>;
      })}
      </div>
    </div>;
}